// Requests waiting for the individual: one-time approvals (FR-23), where a
// company asks to see some details once, and access requests (Phase 9), where
// a company the person consented to looks them up and waits for this approval.
// Approving needs step-up; declining does not. The SMS about a request links here.
import { Badge, Button, Group, Loader, Paper, Stack, Text, Title, VisuallyHidden } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { useStepUp } from "../../shared/session/stepup";
import { FieldBadges, useFormat } from "../../shared/ui/format";
import { FormError, Notice, WidePage } from "../../shared/ui/Page";

type Approval = Schemas["Approval"];

export function ApprovalsPage() {
  return (
    <SignedIn>
      <Approvals />
    </SignedIn>
  );
}

/** useNow ticks once a second so countdowns stay current. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

export function formatRemaining(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

function Approvals() {
  const { t } = useTranslation();
  const stepUp = useStepUp();
  const queryClient = useQueryClient();
  const approvals = useQuery({ queryKey: ["approvals"], queryFn: () => unwrap(api.GET("/consents/pending")) });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const now = useNow();
  const f = useFormat();
  const items = approvals.data?.items ?? [];

  const decided = (a: Approval, approved: boolean) => {
    setNotice(
      approved
        ? t("approvals.approved", { company: a.company.name })
        : t("approvals.declined", { company: a.company.name }),
    );
    void queryClient.invalidateQueries({ queryKey: ["approvals"] });
  };
  const approve = async (a: Approval) => {
    setError(null);
    setNotice(null);
    const token = await stepUp("consent_change");
    if (!token) return;
    const { data, error } = await api.POST("/consents/pending/{approval_id}/approve", {
      params: { path: { approval_id: a.id }, header: { "X-Step-Up-Token": token } },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    decided(a, true);
  };
  const decline = async (a: Approval) => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.POST("/consents/pending/{approval_id}/decline", {
      params: { path: { approval_id: a.id } },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    decided(a, false);
  };

  return (
    <WidePage title={t("approvals.title")} intro={t("approvals.intro")}>
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {approvals.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : approvals.isError ? (
        <FormError message={errorMessage(approvals.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="approvals-empty">
          {t("approvals.empty")}
        </Text>
      ) : (
        <Stack gap="sm" component="ul" p={0} m={0} style={{ listStyle: "none" }} aria-label={t("approvals.list")}>
          {items.map((a) => {
            const left = Date.parse(a.expires_at) - now;
            const expired = left <= 0 || a.status !== "pending";
            return (
              <li key={a.id}>
                <Paper withBorder p="md" radius="md" data-testid="approval">
                  <Stack gap="xs">
                    <Group justify="space-between" align="flex-start">
                      <Title order={2} size="h4">
                        {a.company.name}
                      </Title>
                      {expired ? (
                        <Badge color="gray">{t("approvals.expired")}</Badge>
                      ) : (
                        <Badge color="orange" variant="light" data-testid="approval-countdown">
                          {/* The ticking value is hidden from screen readers; they get the fixed time. */}
                          <span aria-hidden="true">{t("approvals.expires_in", { time: formatRemaining(left) })}</span>
                          <VisuallyHidden>
                            {t("approvals.expires_at", { time: f.dateTime(a.expires_at) })}
                          </VisuallyHidden>
                        </Badge>
                      )}
                    </Group>
                    {a.kind === "access" && (
                      <Text size="sm" data-testid="approval-kind">
                        {t("approvals.access_lookup", { company: a.company.name })}
                      </Text>
                    )}
                    <FieldBadges fields={a.fields} />
                    <Text size="sm">{t("approvals.purpose", { purpose: a.purpose_label || a.purpose })}</Text>
                    {a.kind === "access" && (
                      <Text size="xs" c="dimmed">
                        {t("approvals.access_note")}
                      </Text>
                    )}
                    {!expired && (
                      <Group gap="xs">
                        <Button
                          size="xs"
                          onClick={() => void approve(a)}
                          aria-label={t("approvals.approve_for", { company: a.company.name })}
                        >
                          {t("approvals.approve")}
                        </Button>
                        <Button
                          size="xs"
                          variant="default"
                          onClick={() => void decline(a)}
                          aria-label={t("approvals.decline_for", { company: a.company.name })}
                        >
                          {t("approvals.decline")}
                        </Button>
                      </Group>
                    )}
                  </Stack>
                </Paper>
              </li>
            );
          })}
        </Stack>
      )}
    </WidePage>
  );
}
